import type { Tutorial } from '../../shared/types.js';

export const claudeMaxTutorial: Tutorial = {
  name: 'claude-max',
  title: 'Register a Claude Max subscription key',
  description:
    'Walk through registering your personal Claude Max credential so agents you launch use your own subscription quota.',
  category: 'model-providers',
  docPages: ['administration/agent-model-configuration'],
  agenda: [
    'Open Credentials in Connect',
    'Start the Claude Max login',
    'Run the command on your computer',
    'Register the credential',
  ],
  steps: [
    {
      id: 'intro',
      kind: 'info',
      title: "Let's set up Claude Max",
      body: "This tutorial registers your personal Claude Max subscription so agents you launch run on your own quota instead of an organization API key. We'll walk from here to the Credentials page in Connect.",
      // Start at the top level so the Connect entry is visible in the sidebar.
      path: '/runs',
    },
    {
      id: 'open-connect',
      kind: 'action',
      trigger: 'click',
      title: 'Open Connect',
      body: 'Click "Connect" at the bottom of the sidebar. Model credentials and subscriptions live here.',
      target: '[data-tutorial-id="nav-connect"]',
      placement: 'right',
    },
    {
      id: 'open-credentials',
      kind: 'action',
      trigger: 'click',
      title: 'Open Credentials',
      body: 'Click "Credentials" in the sidebar to manage model provider keys and subscriptions available to agents you launch.',
      target: '[data-tutorial-id="nav-model-providers"]',
      placement: 'right',
      // Safety net: land on the Connect hub (where the Credentials entry
      // renders) even if the user navigated away.
      path: '/configure/connect/credentials',
    },
    {
      id: 'find-subscriptions',
      kind: 'reveal',
      title: 'Find Subscriptions',
      body: 'Scroll down to the Subscriptions section. The bar fills as you bring the Claude Max credential button - the one you will use next - to the center of the screen. Once it is centered we continue automatically.',
      target: '[data-testid="subscriptions-section"]',
      focusTarget: '[data-tutorial-id="claude-max-register"]',
      placement: 'top',
      path: '/configure/connect/credentials/personal/model-providers',
      // Count down 3, 2, 1 inside the ring once the button is centered.
      dwellMs: 3000,
    },
    {
      id: 'open-register-dialog',
      kind: 'action',
      trigger: 'click',
      title: 'Open the Claude Max dialog',
      body: 'On the Claude Max card, click the highlighted button - "Register Credential" if you have not set one up yet, or "Update Credential" if you have - to open the dialog.',
      target: '[data-tutorial-id="claude-max-register"]',
      placement: 'top',
    },
    {
      id: 'copy-command',
      kind: 'action',
      trigger: 'click',
      title: 'Copy the command',
      body: 'Pick your OS in the tabs so the dialog tailors the command to your machine, then click the copy button to put it on your clipboard. You will run it on your own computer in the next step - nothing here reads your machine for you.',
      target: '[data-tutorial-id="claude-max-copy-command"]',
      placement: 'bottom',
      // The OS picker is context for this step, not a step of its own: show it
      // as an inline note that highlights the tabs while the real action stays
      // the copy button.
      notes: [
        {
          id: 'pick-os',
          title: 'Pick your OS',
          body: 'Use the tabs to choose macOS, Linux, or Windows. The dialog tailors the command it shows you to read the right credential file on your machine.',
          target: '[data-tutorial-id="claude-max-platform-tabs"]',
          placement: 'top',
        },
      ],
    },
    {
      id: 'run-locally',
      kind: 'info',
      title: 'Now do this on your computer',
      body:
        'This part happens on your own machine, not in the browser - the command reads a credential file that only exists locally. Switch to your computer and:\n\n' +
        '1. Open Claude Code and run `/login` so your credential is freshly issued. The token captured here does not refresh inside the devbox the way it does locally, so a fresh login keeps the session valid as long as possible.\n' +
        '2. Paste the command you just copied into a terminal and run it.\n' +
        '3. Copy the full JSON it prints - it starts with `{"claudeAiOauth"`.\n\n' +
        "Once you've done all three, click **Next** to paste it in here.",
      // Anchor the checklist to the box the result lands in, so it's clear where
      // the JSON you're about to fetch will go.
      target: '#credential-json',
      placement: 'top',
    },
    {
      id: 'paste-output',
      kind: 'action',
      trigger: 'input',
      title: 'Paste the credential JSON',
      body: 'Paste the full JSON output you just copied into the box below. The dialog validates the shape before submitting.',
      target: '#credential-json',
      placement: 'top',
    },
    {
      id: 'submit',
      kind: 'action',
      trigger: 'click',
      title: 'Register the credential',
      body: 'Click Register to encrypt and save your credential. Agents you launch will now use your personal Claude Max subscription.',
      target: '[data-tutorial-id="claude-max-submit"]',
      placement: 'top',
    },
    {
      id: 'done',
      kind: 'info',
      title: 'All set',
      body: 'Your Claude Max credentials are ready to use. The card shows as Configured - you can revisit this page any time to update or delete it, and only you can see it (organization admins cannot).',
      target: '[data-testid="claude-max-card"]',
      placement: 'top',
      // The dialog closes on submit, so highlight the configured card back on
      // the Model Providers page.
      path: '/configure/connect/credentials/personal/model-providers',
    },
  ],
};
